import { useMutation, useQueryClient } from '@tanstack/react-query';
import { MutationOptions, MutationOptionsWithResponse } from '../../../services/general.types';
import { useIntegrationService } from '../../../services/integration.service';
import { QUERY_KEYS } from '../../../utils/queryKeys';

interface CreateExchangeIntegrationParams {
    importerUri: string;
    data: any;
}

interface DeleteIntegrationParams {
    name: string;
    jobId: number;
    jobTableName: string;
}

interface ImportCsvParams {
    importerUri: string;
    file: File;
}

export const useIntegrationMutations = () => {
    const queryClient = useQueryClient();
    const integrationService = useIntegrationService();

    const invalidateQueries = () => {
        QUERY_KEYS.allQueries().forEach((key) => {
            queryClient.invalidateQueries({ queryKey: [key] });
        });
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.REFRESH_BALANCE] });
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.RECALCULATION] });
    };

    const updateIntegrationSetupState = () => {
        // Invalidate the integration setup state query to update the UI
        queryClient.invalidateQueries({ queryKey: ['integrationSetupState'] });
    };

    // Force a fresh fetch after delete operations to prevent stale state
    const forceRefreshIntegrations = () => {
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.INTEGRATIONS] });
        // Also force a refetch to ensure we get fresh data
        queryClient.refetchQueries({ queryKey: [QUERY_KEYS.INTEGRATIONS] });
    };

    // Create Exchange Integration
    const { mutate: baseCreateExchangeIntegration, isPending: isExchangeCreating } = useMutation({
        mutationKey: ['createIntegration'],
        mutationFn: (params: CreateExchangeIntegrationParams) =>
            integrationService.createExchangeIntegration(params.importerUri, params.data),
        onSuccess: () => {
            invalidateQueries();
            updateIntegrationSetupState();
        },
        onError: (error) => {
            invalidateQueries();
        },
    });

    const createExchangeIntegration = (data: CreateExchangeIntegrationParams, options?: MutationOptions) => {
        baseCreateExchangeIntegration(data, {
            onSuccess: () => {
                options?.onSuccess?.();
            },
            onError: options?.onError,
        });
    };

    // Create Wallet Integration
    const { mutate: baseCreateWalletIntegration, isPending: isWalletCreating } = useMutation({
        mutationKey: ['createIntegration'],
        mutationFn: (params: any) => integrationService.createWalletIntegration(params),
        onSuccess: () => {
            invalidateQueries();
            updateIntegrationSetupState();
        },
        onError: (error) => {
            invalidateQueries();
        },
    });

    const createWalletIntegration = (data: any, options?: MutationOptions) => {
        baseCreateWalletIntegration(data, {
            onSuccess: () => {
                options?.onSuccess?.();
            },
            onError: options?.onError,
        });
    };

    // Delete Integration
    const { mutate: baseDeleteIntegration, isPending: isDeleting } = useMutation({
        mutationKey: ['deleteIntegration'],
        mutationFn: (params: DeleteIntegrationParams) =>
            integrationService.deleteIntegration(params.name, params.jobId, params.jobTableName),
        onSuccess: () => {
            // Optimistic update: immediately show recalculation state
            // This provides instant feedback while the backend processes
            queryClient.setQueryData([QUERY_KEYS.RECALCULATION], (old: any) => ({
                ...old,
                run: true, // Optimistically assume recalculation will start
                new: false
            }));
            
            // Force immediate refresh to prevent stale state issues
            forceRefreshIntegrations();
            invalidateQueries();
            updateIntegrationSetupState();
            
            // Invalidate recalculation to check for actual status after deletion
            setTimeout(() => {
                queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.RECALCULATION] });
            }, 1000);
            
            // Add a delayed refresh as backup in case backend is slow
            setTimeout(() => {
                forceRefreshIntegrations();
                queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.RECALCULATION] });
            }, 3000);
        },
        onError: (error) => {
            console.error('Failed to delete integration:', error);
            // Still refresh on error to clear any inconsistent state
            forceRefreshIntegrations();
            // Invalidate recalculation status on error too
            queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.RECALCULATION] });
        },
    });

    const deleteIntegration = (data: DeleteIntegrationParams, options?: MutationOptions) => {
        baseDeleteIntegration(data, {
            onSuccess: () => {
                options?.onSuccess?.();
            },
            onError: options?.onError,
        });
    };

    // Import CSV
    const { mutate: baseImportCsv, isPending: isCsvImporting } = useMutation({
        mutationKey: ['integrationImportCsv'],
        mutationFn: (params: ImportCsvParams) => {
            return integrationService.importCsv(params.importerUri, params.file, [], () => {});
        },
        onSuccess: (response) => {
            invalidateQueries();
            updateIntegrationSetupState();
        },
        onError: (error) => {
            invalidateQueries();
        },
    });

    const importCsv = (data: ImportCsvParams, options?: MutationOptionsWithResponse) => {
        baseImportCsv(data, {
            onSuccess: (response) => {
                options?.onSuccess?.(response); // Pass the response to onSuccess callback
            },
            onError: options?.onError,
        });
    };

    return {
        createExchangeIntegration,
        createWalletIntegration,
        deleteIntegration,
        importCsv,
        isExchangeCreating,
        isWalletCreating,
        isCsvImporting,
        isDeleting,
    };
};
